import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { environment } from '../../../environments/environment';

function allowedPlatformOrigin(): string {
  try {
    return new URL(environment.nSidePlatformUrl).origin;
  } catch {
    return '';
  }
}

function isLocalhost(host: string): boolean {
  return host === 'localhost' || host === '127.0.0.1' || host === '';
}

/**
 * The EOP app is embedded inside the nSide platform. It must only run when
 * opened within the platform origin for the current environment:
 *   development -> https://dev.nside.io
 *   staging     -> https://staging.nside.io
 *   production  -> https://app.nside.io
 * (all derived from environment.nSidePlatformUrl).
 */
export function isOpenedInsidePlatform(): boolean {
  // SSR / prerender: no window — re-checked once running in the browser.
  if (typeof window === 'undefined') return true;

  const allowed = allowedPlatformOrigin();
  // Misconfigured environment (no platform url) — don't hard-block.
  if (!allowed) return true;

  // Local development convenience: allow running standalone on localhost.
  if (!environment.production && isLocalhost(window.location.hostname)) {
    return true;
  }

  // Preferred signal: the browser-reported embedder (ancestor) origins.
  const ancestors = (window.location as any).ancestorOrigins as DOMStringList | undefined;
  if (ancestors && ancestors.length > 0) {
    for (let i = 0; i < ancestors.length; i++) {
      if (ancestors[i] === allowed) return true;
    }
    return false;
  }

  // Fallback for browsers without ancestorOrigins (e.g. Firefox): the referrer
  // set by the embedding platform page.
  if (document.referrer) {
    try {
      return new URL(document.referrer).origin === allowed;
    } catch {
      return false;
    }
  }

  // No embedding information available → not allowed.
  return false;
}

export const nsidePlatformGuard: CanActivateFn = () => {
  const router = inject(Router);
  return isOpenedInsidePlatform() ? true : router.parseUrl('/unauthorized');
};
